// useLeadPolicyEditorData.js – data hook for Lead Policy Editor (fixed workGroups mapping)
// Framework: React 18

import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import axios from "../../config/axiosInstance";

const useLeadPolicyEditorData = (hostUrl, eventId, visible = true) => {
    // ---------- state ---------- //
    const [programmePoints, setProgrammePoints] = useState([]);
    const [participantTypes, setParticipantTypes] = useState([]);
    const [participantStates, setParticipantStates] = useState([]);
    const [workGroups, setWorkGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    // ---------- TOKEN ---------- //
    const fetchToken = async () => {
        if (!hostUrl) return null;
        try {
            const { data } = await axios.get("/regasus/token", { params: { host: hostUrl } });
            return data.access_token;
        } catch {
            message.error("Error obtaining token");
            return null;
        }
    };

    // ---------- individual fetchers ---------- //
    const fetchProgrammePoints = async (token) => {
        const { data } = await axios.get(`/regasus/event-pps/${eventId}`, { params: { token, host: hostUrl } });
        return data.programmePoints?.programmePoint || [];
    };

    const fetchParticipantTypes = async (token) => {
        const { data } = await axios.get(`/regasus/event-participant-types/${eventId}`, { params: { token, host: hostUrl } });
        return data.participantTypes?.participantType || [];
    };

    const fetchParticipantStates = async (token) => {
        try {
            const { data } = await axios.get(`/regasus/participant-states/`, { params: { token, host: hostUrl } });
            return data.participantStates?.participantState || [];
        } catch {
            return [];
        }
    };

    const rawFetchProgrammeOfferingsByPP = async (token, programmePointId) => {
        const { data } = await axios.get(`/regasus/programme-point-offerings/${programmePointId}`, { params: { token, host: hostUrl } });
        return data.programmeOfferings?.programmeOffering || [];
    };

    const fetchProgrammeOfferingsByPP = async (programmePointId) => {
        const token = await fetchToken();
        if (!token || !programmePointId) return [];

        try {
            let offerings = await rawFetchProgrammeOfferingsByPP(token, programmePointId);

            // order by position asc
            offerings = offerings.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

            // enrich → add readable name, descriptionEN y PO_Name
            return offerings.map((off) => {
                const pt = participantTypes.find((p) => p.id === off.participantTypeID);
                const readable = pt?.name?.[0]?.value || `PT-${off.participantTypeID}`;

                // take description in English if it exists; otherwise, the first element
                const descObj = off.description?.find((d) => d.lang === "en") || off.description?.[0] || {};
                const desc = descObj.value || "";

                const amount = off.price?.amountBrutto ?? off.totalPrice?.amountBrutto ?? 0;
                const currency = off.price?.currency ?? off.totalPrice?.currency ?? '';

                const PO_Name = desc
                    ? `${desc}, ${readable}, ${amount} ${currency}`
                    : `${readable}, ${amount} ${currency}`;

                return {
                    ...off,
                    PO_Name: `${PO_Name}`,
                };
            });
        } catch (err) {
            console.warn("fetchProgrammeOfferingsByPP", programmePointId, err?.message || err);
            return [];
        }
    };

    const fetchWorkGroups = async (token) => {
        try {
            const { data } = await axios.get(`/regasus/event-workgroups/${eventId}`, { params: { token, host: hostUrl } });
            return data.workGroups?.workGroup || [];
        } catch {
            return [];
        }
    };

    // ---------- master fetch ---------- //
    const fetchAllCatalogues = useCallback(async () => {
        if (!hostUrl || !eventId) return;
        setLoading(true);
        try {
            const token = await fetchToken();
            if (!token) return;

            const [pps, pts, pss, wgs] = await Promise.all([
                fetchProgrammePoints(token),
                fetchParticipantTypes(token),
                fetchParticipantStates(token),
                fetchWorkGroups(token),
            ]);

            setProgrammePoints(pps);
            setParticipantTypes(pts);
            setParticipantStates(pss);
            setWorkGroups(wgs);
        } catch {
            message.error("Error loading event catalogs");
        } finally {
            setLoading(false);
        }
    }, [hostUrl, eventId]);

    // ---------- trigger ---------- //
    useEffect(() => {
        if (visible) fetchAllCatalogues();
    }, [visible, fetchAllCatalogues]);

    // ---------- API ---------- //
    return {
        programmePoints,
        participantTypes,
        participantStates,
        workGroups,
        loading,
        refetch: fetchAllCatalogues,
        fetchProgrammeOfferingsByPP,
    };
};

export default useLeadPolicyEditorData;
